// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { ellipsis, xScreen } from "@synd/XDL/Utilities";
import { mergeWithCss } from "@synd/XDL/Utilities/mergeWithCss";
import { css } from "styled-components";

interface ProductDescriptionVariantStyles {
    small?: ProductDescriptionStyles;
    medium?: ProductDescriptionStyles;
    large?: ProductDescriptionStyles;
}
const _sharedLinkStyle = css`
    margin-bottom: 2px;
    align-items: flex-start;
    -webkit-box-align: start;
    width: fit-content;
    &:hover > ${StyledTypography} {
        text-decoration: underline;
    }
`;
const sharedStyles: { [key in keyof ProductDescriptionVariantStyles]: StyledProp } = {
    small: css`
        ${_sharedLinkStyle}
    `,
    medium: css`
        ${_sharedLinkStyle}
    `,
    large: css`
        ${_sharedLinkStyle}
    `,
};
const sharedSmallTextStyles: TypographyProps = {
    weight: fontConfig.weight.regular,
    color: "text.main",
    css: css`
        font-size: ${fontConfig.xSmallSingleLine.size}px;
        letter-spacing: 0;
        line-height: ${fontConfig.xSmallSingleLine.lineHeight};
        vertical-align: unset;
        min-height: 34px;
        margin-bottom: 2px;
        ${wrapInContainerStyles}
        ${ellipsis(3)}
    `,
};
const sharedMediumTextStyles: TypographyProps = {
    weight: fontConfig.weight.semiBold,
    color: "text.main",
    css: css`
        font-size: ${fontConfig.smallSingleLine.size}px;
        letter-spacing: 0;
        line-height: ${fontConfig.smallSingleLine.lineHeight};
        vertical-align: unset;
        margin-bottom: 2px;
        ${ellipsis(2)}
        ${xScreen("lte", "md")} {
            ${ellipsis(3)}
        }
        ${wrapInContainerStyles}
    `,
};

const sharedLargeTextStyles: TypographyProps = {
    weight: fontConfig.weight.semiBold,
    color: "text.main",
    css: css`
        font-size: ${fontConfig.regularSingleLine.size}px;
        letter-spacing: 0;
        line-height: ${fontConfig.regularSingleLine.lineHeight};
        vertical-align: unset;
        margin-bottom: 2px;
        ${wrapInContainerStyles}
    `,
};

const productDescriptionVariantStyles: ProductDescriptionVariantStyles = {
    small: {
        productDetailDescription: {
            ...mergeWithCss(sharedSmallTextStyles, {
                css: css`
                    ${sharedStyles.small}
                `,
            }),
        },
        productDetailLink: {
            mergeCss: true,
            ...mergeWithCss(
                {
                    color: "text.main",
                    typographyProps: {
                        ...sharedSmallTextStyles,
                    },
                    css: css`
                        width: auto;
                    `,
                },
                { css: sharedStyles.small },
            ),
        },
    },
    medium: {
        productDetailDescription: {
            ...mergeWithCss(sharedMediumTextStyles, { css: sharedStyles.medium }),
        },
        productDetailLink: {
            mergeCss: true,
            ...mergeWithCss(
                {
                    color: "text.main",
                    typographyProps: {
                        ...sharedMediumTextStyles,
                    },
                    css: css`
                        width: auto;
                    `,
                },
                { css: sharedStyles.medium },
            ),
        },
    },
    large: {
        productDetailDescription: {
            ...mergeWithCss(sharedLargeTextStyles, { css: sharedStyles.large }),
        },
        productDetailLink: {
            mergeCss: true,
            ...mergeWithCss(
                {
                    color: "text.main",
                    typographyProps: {
                        ...sharedLargeTextStyles,
                    },
                    css: css`
                        width: auto;
                    `,
                },
                { css: sharedStyles.large },
            ),
        },
    },
};

export default productDescriptionVariantStyles;
